import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Popover } from '@mantine/core';
import { Icon } from '@shakkerai/components';
import { useUserInfo, setTempState, useTempState, getTasks, removeTask, updateCurrentTask } from '@starflow/app/store';
import { useUserVipInfo } from '@shakkerai/pro-commercial';
import { registerComponent } from '@starflow/app/extension';
import { clearUsertokenToCookie, setUsertokenToCookie } from '@shakkerai/util-cookie';
import { setUserinfoToLocalStorage } from '@shakkerai/util-localstorage/src/auth';
import { useGenHistoryVisible } from '@shakkerai/pro-gen-history/src/store/base';
import { GenHistory, ImgsViewerWrapper } from '@shakkerai/pro-gen-history';
import { translate } from '@shakkerai/util-i18n';
import { useClickOutside } from '@mantine/hooks';
import { config } from '@shakkerai/config';
import { isMainland } from '@shakkerai/pro-utils';
import { useGuidanceRestartHooks } from '@shakkerai/pro-guidance';
import { RightMenuItem, RightMenu, Dropdown, Text } from '@shakkerai/pro-components';
import powerIcon from '@shakkerai/pro-components/src/button/power.svg';
import { convert2CompressedUrl } from '@shakkerai/util-image';
import styles from './navbar.module.scss';
import './navbar.scss';
import { executeNextTask } from '../../../helpers/process';

registerComponent('root', { Component: GenHistory });
registerComponent('root', { Component: ImgsViewerWrapper });

const Profile = React.memo(({ userInfo, onLogout }) => {
  const { guidanceEntryVisible, closeGuidanceEntry } = useGuidanceRestartHooks();
  const [opened, setOpened] = useState(false);
  const ref = useClickOutside(() => setOpened(false));

  const handleClearFrozenTask = useCallback(async () => {
    const tasks = getTasks();
    const { map, current } = tasks || {};

    if (current) {
      const currentTask = map?.[current];

      if (currentTask) {
        if (currentTask.status === 'idle' || currentTask.status === 'error') {
          // 对于闲置或者错误状态的任务，直接清理掉
          removeTask(map[current].id);
          updateCurrentTask(null);
        } else if (
          currentTask.hasSubTasks &&
          currentTask.results &&
          currentTask.results.every(
            // 如果所有子任务都是在异常状态，也清理掉
            (item) => item.status === 'idle' || item.status === 'error',
          )
        ) {
          removeTask(map[current].id);
          updateCurrentTask(null);
        }
      } else {
        updateCurrentTask(null);
      }
    }
  }, []);

  useEffect(() => {
    if (guidanceEntryVisible) {
      setOpened(true);
      setTimeout(() => {
        closeGuidanceEntry();
      }, 4500);
    } else {
      setOpened(false);
    }
    // eslint-disable-next-line
  }, [guidanceEntryVisible]);

  return (
    <Popover
      position="bottom-end"
      opened={opened}
      withArrow
      offset={{ mainAxis: 18, crossAxis: 0 }}
      arrowSize={10}
      arrowRadius={1}
    >
      <Popover.Target>
        <div
          ref={ref}
          onClick={() => setOpened(true)}
          className="flex items-center justify-center mx-3 w-7 h-7 bg-white shadow-light rounded-full cursor-pointer"
        >
          <img src={convert2CompressedUrl(userInfo.avatar, 56)} className="w-7 h-7 rounded-full object-cover" />
        </div>
      </Popover.Target>
      <Dropdown className="!border-none !bg-[#2c2c2c] !p-0">
        <RightMenu
          visible={true}
          style={{
            width: 120,
            zIndex: 1,
          }}
        >
          <RightMenuItem onClick={onLogout}>{translate('user_logout')}</RightMenuItem>
          <RightMenuItem onClick={handleClearFrozenTask}>{translate('clear_forzen_task')}</RightMenuItem>
        </RightMenu>
      </Dropdown>
    </Popover>
  );
});

export const User = React.memo(() => {
  const { userInfo, updateUserInfo } = useUserInfo();
  const { toggleHistoryModelsBar } = useGenHistoryVisible();
  const [isShowSharePicPopover, setIsShowSharePicPopover] = useState(false);
  const userLogin = !!userInfo?.id;

  const goHome = () => {
    if (config && config.env === 'aws') {
      window.location.href = '/';
    } else {
      window.location.href = '/';
    }
  };

  const handleLogOut = () => {
    clearUsertokenToCookie();
    updateUserInfo({ userInfo: null });
    setUsertokenToCookie(null);
    setUserinfoToLocalStorage(null);

    if (window.location.pathname === '/pro') {
      window.location.reload();
    } else {
      goHome();
    }
  };

  // 判断右上角hostory是否需要展示【图片分享】的提示弹窗
  const isShowSharePicTips = useTempState('isShowSharePicTips');
  useEffect(() => {
    if (isShowSharePicTips) {
      setIsShowSharePicPopover(true);
    }
  }, [isShowSharePicTips]);

  const { remainedPower, loadVipInfo } = useUserVipInfo();

  useEffect(() => {
    loadVipInfo();
    // eslint-disable-next-line
  }, []);

  const vipNode = useMemo(() => {
    return (
      <div
        className="flex items-center ml-[16px] cursor-pointer"
        onClick={() => {
          if (isMainland) {
            // window.open('https://www-test.liblib.art/viphome');
            window.open('https://www.liblib.art/viphome'); // 生产更换
          } else {
            window.open('/purchase?source=pro');
          }
        }}
      >
        <img src={powerIcon} className="w-2.5 mr-1" alt="" />
        <div className={styles.vipRemainPoint}>{remainedPower}</div>
      </div>
    );
  }, [remainedPower]);

  return (
    <div className="flex items-center justify-end h-full">
      {vipNode}
      {userLogin && <Profile userInfo={userInfo} onLogout={handleLogOut} />}
      {userLogin && (
        <Popover
          opened={isShowSharePicPopover}
          arrowPosition="side"
          arrowOffset={27}
          onChange={setIsShowSharePicPopover}
          clickOutsideEvents={['mouseup', 'touchend']}
          withArrow
          position="bottom-end"
          offset={19}
          width={323}
        >
          <Popover.Target>
            <button
              onClick={() => (toggleHistoryModelsBar(), setTempState({ showNormalCkptGide: false }))}
              data-role="gen-history-trigger"
              className={`flex items-center ml-3`}
            >
              <Icon className="icon-border_reviewing" fontSize={14} color="white" />
              <span className="ml-1 text-[white] text-xs font-semibold">{translate('history_entrance')}</span>
            </button>
          </Popover.Target>
          <Popover.Dropdown className={styles.guidancePopover}>
            <div className={styles.wrapper}>
              {<h5 className={styles.title}>{translate('public_tips_title')}</h5>}
              <Text className={styles.content}>{translate('public_tips_desc')}</Text>
              <button onClick={() => setIsShowSharePicPopover(false)} className={styles.btn}>
                {translate('guidance_got_button')}
              </button>
            </div>
          </Popover.Dropdown>
        </Popover>
      )}
    </div>
  );
});
